<template>
	<div
		:class="[
			'flex flex-column relative input-content',
			!noSpace ? 'gap-2' : null,
		]"
	>
		<!-- Label container -->
		<div class="flex gap-1">
			<label
				v-if="props.label"
				:for="props.id"
				:class="[
					'tv__input-label',
					props.size === 'sm' ? 'tv__input-sm-label' : '',
				]"
			>
				{{ props.label }}
			</label>
			<!-- label tooltip -->
			<TVTooltip
				v-if="props.tooltipMessage"
				:max-width-tooltip="props.tooltipMaxWidth"
				:float-tooltip-mobile="props.tooltipDirMobile"
				:float-tooltip-desk="props.tooltipDirDesk"
			>
				<template #title>
					<p v-html="props.tooltipMessage"></p>
				</template>
			</TVTooltip>
		</div>
		<!-- Input Select -->
		<Dropdown
			v-model="selectedItemValue"
			:options="props.options || []"
			:class="inputClasses"
			:option-label="props.optionLabel || 'text'"
			option-value="value"
			:placeholder="props.placeholder || ''"
			:disabled="props.disabled"
			:auto-filter-focus="!isMobile"
			:editable="props.editable"
			:invalid="props.error || errorMessage || props.customErrorMessage"
			:aria-describedby="props.id ? props.id + '-help' : ''"
			:filter="!props.editable && props.options && props.options.length > 5"
			:checkmark="props.checkmark"
			:loading="props.loading"
			:pt="{
				filterIcon: () => ({
					style: {
						right: 'unset',
						left: '24px',
					},
				}),
				filterInput: () => ({
					style: {
						padding: '1rem 1rem 1rem 2.25rem',
					},
				}),
			}"
			@change="handleChange"
			@blur="handleBlur"
			translate="no"
			class="notranslate"
		/>
		<!-- Help message -->
		<span :class="[!noSpace ? 'input-message-content' : null]">
			<p
				v-if="props.helpMessage && (!props.customErrorMessage || errorMessage)"
				class="tv__input-help"
			>
				{{ props.helpMessage }}
			</p>
			<!-- Error message -->
			<div
				v-if="errorMessage || props.customErrorMessage"
				class="tv__input-error-message flex"
			>
				<i class="tv__input-icon tv__input-icon-error"></i>
				{{ errorMessage || props.customErrorMessage }}
			</div>
		</span>
	</div>
</template>

<script setup>
import { ref, defineProps, computed, defineEmits, watch } from 'vue'
import { useField } from 'vee-validate'
import Dropdown from 'primevue/dropdown'
import TVTooltip from '../global/TVTooltip.vue'

const props = defineProps({
	name: String,
	value: String,
	label: String,
	placeholder: String,
	id: String,
	disabled: Boolean,
	noSpace: Boolean,
	error: Boolean,
	customErrorMessage: String,
	helpMessage: String,
	editable: Boolean,
	tooltipMessage: String,
	tooltipDirDesk: String,
	tooltipDirMobile: String,
	tooltipMaxWidth: Number,
	acceptEmpty: Boolean,
	modelValue: String,
	checkmark: {
		type: Boolean,
		default: true,
	},
	optionLabel: {
		type: String,
		default: 'text',
	},
	size: {
		type: String,
		default: 'lg',
		validator: (value) => ['lg', 'sm'].includes(value),
	},
	options: {
		type: Array,
	},
	loading: {
		type: Boolean,
		default: false,
	},
})

const emits = defineEmits(['update:modelValue', 'change-value'])

const isMobile = computed(() => {
	return window.innerWidth <= 768
})

const {
	value: inputValue,
	errorMessage,
	handleBlur,
} = useField(props.name || 'default', undefined, {
	initialValue: props.value || '',
})

const selectedItemValue = ref(props.modelValue || '')

const inputClasses = computed(() => [
	'tv__input',
	props.size === 'sm' ? 'tv__input-sm' : null,
	props.editable ? 'tv__input-editable' : null,
])

const handleChange = (value) => {
	emits('change-value')
	inputValue.value = value.value
	emits('update:modelValue', value.value)
}

watch(
	() => props.modelValue,
	(newValue) => {
		if (
			(newValue !== null && newValue !== undefined && newValue !== '') ||
			props.acceptEmpty
		) {
			selectedItemValue.value = newValue
			inputValue.value = newValue
		}
	},
	{ immediate: true },
)

watch(selectedItemValue, (newValue) => {
	if (
		newValue !== null &&
		newValue !== undefined &&
		newValue !== props.modelValue
	) {
		emits('update:modelValue', newValue)
	}
})
</script>
